import { useEffect } from "react";
import Loading from "../../../components/Loading";
import { arrayMap, findItemByFiled } from "../../../utils/array.utils";
import { useGetAllCarInfoQuery, useGetGroupCarInfoQuery } from "../../../utils/carInfo.utils";
import { useGetAllCarInfoTypeQuery } from "../../../utils/carInfoType.utils";
import { RenderFields, optionGen } from "../../../utils/form.utils";
import { textCamelCase } from "../../../utils/string.utils";

export default function BrandField({ watch, control, errors, setValue, setError, clearErrors }) {
  const brandId = watch("brand")?.value;
  const modelId = watch("model")?.value;
  const minMileage = watch("min_mileage");
  const maxMileage = watch("max_mileage");
  const minPrice = watch("min_price");
  const maxPrice = watch("max_price");

  useEffect(() => {
    // Clear previous errors
    clearErrors(["min_mileage", "max_mileage"]);

    if (Number(minMileage) < 0) {
      setValue("min_mileage", 0);
    }

    if (Number(maxMileage) < 0) {
      setValue("max_mileage", 0);
    }

    if (Number(minPrice) < 0) {
      setValue("min_price", 0);
    }

    if (Number(maxPrice) < 0) {
      setValue("max_price", 0);
    }

    // Check if minMileage is greater than maxMileage and maxMileage is not 0
    if (Number(minMileage) > Number(maxMileage) && Number(maxMileage) !== 0) {
      setError("min_mileage", {
        type: "manual",
        message: "Minimum mileage should be less than maximum mileage"
      });
      setError("max_mileage", {
        type: "manual",
        message: "Maximum mileage should be greater than minimum mileage"
      });
    }
  }, [minMileage, maxMileage, setError, clearErrors, setValue, minPrice, maxPrice]);

  const { loading: parentLoading, data: parentData } = useGetGroupCarInfoQuery({
    query: ``,
    key: `GetParentGroupCarInfoForStockListAdd`
  });

  const { isLoading: childLoading, data: childData } = useGetGroupCarInfoQuery({
    query: `?in=0&parent=${modelId}`,
    key: `GetChildGroupCarInfoForStockListAdd`,
    options: {
      enabled: !!modelId
    }
  });

  const { loading: typeLoading, data: typeData } = useGetAllCarInfoTypeQuery({
    query: ``,
    key: `GetTypeDataForStockListAdd`
  });

  function handleTypeId(slug) {
    return typeData?.length ? findItemByFiled(typeData, "slug", slug)?.id : null;
  }

  let typeIds = {};

  const typeSlugs = arrayMap(typeData, (item) => item?.slug) || [];

  typeSlugs.forEach((element) => {
    typeIds[textCamelCase(element)] = handleTypeId(element);
  });

  const modelTypeId = typeIds?.model;

  const { isLoading: modelLoading, data: modelData } = useGetAllCarInfoQuery({
    query: `?parent=${brandId}&type=${modelTypeId}`,
    options: {
      enabled: !!brandId
    },
    key: "GetModelDataForStockListAdd"
  });

  function handleParentData(name) {
    return parentData[name] ? optionGen(parentData[name]) : [];
  }

  function handleChildData(name) {
    return childData[name] ? optionGen(childData[name]) : [];
  }

  const brandOptions = handleParentData("brand");
  const colorOptions = handleParentData("color");
  const ccOptions = handleParentData("cubic-capacity-(cc)");
  const gradeOptions = handleParentData("auction-grade");
  const fuelOptions = handleParentData("fuel");
  const yearOptions = handleParentData("year");
  const bodyTypeOptions = handleParentData("body-type");

  const modelOptions = modelData?.length ? optionGen(modelData) : [];

  const packageOptions = handleChildData("package");
  const featureOptions = handleChildData("features");

  const fields = [
    {
      label: "Brand",
      name: "brand",
      type: "select",
      placeholder: "Select a brand",
      col: { md: 12 },
      options: brandOptions
    },
    {
      label: "Model",
      name: "model",
      type: "select",
      placeholder: "Select a model",
      col: { md: 12 },
      options: modelOptions
    },
    {
      label: "Package",
      name: "package",
      type: "select",
      placeholder: "Select a package",
      col: { md: 12 },
      options: packageOptions
    },
    {
      label: "Color",
      name: "color",
      type: "select",
      placeholder: "Select a color",
      col: { md: 12 },
      options: colorOptions
    },
    {
      label: "Cubic Capacity (CC)",
      name: "cc",
      type: "select",
      placeholder: "Select a cc",
      col: { md: 12 },
      options: ccOptions
    },
    {
      label: "Auction Grade",
      name: "grade",
      type: "select",
      placeholder: "Select a grade",
      col: { md: 12 },
      options: gradeOptions
    },
    {
      label: "Fuel",
      name: "fuel",
      type: "select",
      placeholder: "Select a fuel",
      col: { md: 12 },
      options: fuelOptions
    },
    {
      label: "Min Mileage",
      name: "min_mileage",
      type: "number",
      placeholder: "Enter min mileage",
      col: { xs: 6 }
    },
    {
      label: "Max Mileage",
      name: "max_mileage",
      type: "number",
      placeholder: "Enter max mileage",
      col: { xs: 6 }
    },
    {
      label: "Min Price",
      name: "min_price",
      type: "number",
      placeholder: "Enter min price",
      col: { xs: 6 }
    },
    {
      label: "Max Price",
      name: "max_price",
      type: "number",
      placeholder: "Enter max price",
      col: { xs: 6 }
    },
    {
      label: "Min Date",
      name: "min_date",
      type: "date",
      placeholder: "Enter min date",
      col: { xs: 12 }
    },
    {
      label: "Max Date",
      name: "max_date",
      type: "date",
      placeholder: "Enter max date",
      col: { xs: 12 }
    },
    {
      label: "Year",
      name: "year",
      type: "select",
      placeholder: "Select a year",
      col: { md: 12 },
      options: yearOptions
    },
    {
      label: "Features",
      name: "features",
      type: "select",
      placeholder: "Select features",
      col: { md: 12 },
      options: featureOptions,
      isMulti: true
    },
    {
      label: "Body Type",
      name: "body_type",
      type: "select",
      placeholder: "Select a body type",
      col: { md: 12 },
      options: bodyTypeOptions
    }
  ];

  const isLoading = typeLoading || modelLoading || parentLoading || childLoading;

  return (
    <>
      <Loading loading={isLoading} />
      {
        <RenderFields
          setValue={setValue}
          fields={fields}
          control={control}
          watch={watch}
          errors={errors}
        />
      }
    </>
  );
}
